/**
 * Lot D (Eric 2026-06-30) — Interrupteurs d'urgence du pipeline.
 *
 * La diarisation est restee coupee du 23/09 pendant un mois sans que cela
 * apparaisse sur un seul ecran. Cette carte montre l'etat de chaque
 * interrupteur, depuis quand et par qui — et signale ceux qui trainent.
 */

import { useLang } from "../../contexts/LanguageContext";

// Tâches 1362 · 1363 (Eric 2026-06-30) — Les deux langues sur la MÊME ligne :
// deux tables jumelles divergent au premier interrupteur ajouté.
const LIBELLES = {
  skip_assemblyai_diarization: ["Diarisation (AssemblyAI)", "Diarization (AssemblyAI)"],
  skip_visual_speaker_id: ["Identification faciale (Vision)", "Facial identification (Vision)"],
  skip_whisper_transcription: ["Transcription Whisper", "Whisper transcript"],
};

export const KillSwitchesCard = ({ initial, awaiting }) => {
  const { tr, lang } = useLang();
  // Meme piege que la carte Cle maitresse : la prop arrive apres le premier
  // rendu, donc on la LIT au lieu de la figer dans un etat initial.
  const data = initial || null;
  const flags = data?.flags || [];

  return (
    <div
      className="rounded-xl border border-border bg-card p-5 space-y-4"
      data-testid="kill-switches-card"
    >
      <div>
        <h3 className="text-lg font-bold flex items-center gap-2">
          <span
            className={`inline-block w-2.5 h-2.5 rounded-full ${
              !data ? "bg-zinc-400" : data.ok ? "bg-emerald-500" : "bg-amber-500"
            }`}
          />
          {tr("Interrupteurs d'urgence du pipeline", "Pipeline emergency switches")}
        </h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          {tr(
            `Un interrupteur actif contourne son étape pour TOUTES les organisations. Au-delà de ${data?.stale_after_hours ?? 24} h, il est signalé : une mesure d'urgence n'est pas censée durer.`,
            `An active switch bypasses its step for EVERY organisation. Past ${data?.stale_after_hours ?? 24} h it is flagged: an emergency measure is not meant to last.`,
          )}
        </p>
      </div>

      {!data && (
        <p className="text-sm text-muted-foreground" data-testid="kill-switches-empty">
          {awaiting ? tr("Chargement…", "Loading…") : tr("État indisponible.", "State unavailable.")}
        </p>
      )}

      {flags.map((f) => (
        <div
          key={f.name}
          className={`rounded-lg border p-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between ${
            f.stale ? "border-amber-400 bg-amber-50" : "border-border"
          }`}
          data-testid={`kill-switch-${f.name}`}
        >
          <div className="min-w-0">
            <div className="text-sm font-semibold">{(LIBELLES[f.name] || [])[lang === "en" ? 1 : 0] || f.name}</div>
            <div className="text-xs text-muted-foreground">
              {f.value
                ? tr(
                    `Coupée depuis ${f.hours_on ?? "?"} h — par ${f.by || "inconnu"}`,
                    `Off for ${f.hours_on ?? "?"} h — by ${f.by || "unknown"}`,
                  )
                : tr("Étape active (normal)", "Step active (normal)")}
            </div>
          </div>
          <span
            className={`flex-shrink-0 self-start px-2 py-1 rounded-md text-xs font-bold ${
              f.value ? "bg-amber-500 text-white" : "bg-emerald-100 text-emerald-800"
            }`}
          >
            {f.value ? tr("CONTOURNÉE", "BYPASSED") : tr("ACTIVE", "ACTIVE")}
          </span>
        </div>
      ))}

      {data && !data.ok && (
        <p className="text-xs text-amber-800" data-testid="kill-switches-warning">
          {tr(
            "Un interrupteur laissé actif produit des résultats dégradés qui ressemblent à des résultats normaux : une vidéo diarisée avec un seul locuteur ne dit pas qu'elle n'a pas été diarisée.",
            "A switch left on produces degraded results that look like normal ones: a diarized video with a single speaker never says it was not diarized.",
          )}
        </p>
      )}
    </div>
  );
};

export default KillSwitchesCard;
